import { Box, Fade } from '@mui/material'
import cn from 'classnames'
import LottieSuccessIcon from 'ext-ui/components/Dropdown/components/LottieSuccessIcon/LottieSuccessIcon'
import Processing from 'ext-ui/components/Processing/Processing'
import TextInputInternal from 'ext-ui/components/TextInput/TextInputInternal/TextInputInternal'
import TooltipIcon, {
  TooltipIconProps,
} from 'ext-ui/components/TooltipIcon/TooltipIcon'
import {
  LoadingState,
  useLoadingStateAnimation,
} from 'ext-ui/hooks/useLoadingStateAnimation'
import React, { useId, useImperativeHandle, useRef } from 'react'
import { useTranslate } from 'src/localization/Trns'

import style from './TextInput.style'

const renderErrors = (error: React.ReactNode): React.ReactNode => {
  if (!error) return null

  if (Array.isArray(error)) {
    return error.map((err, index) => <div key={index}>{err}</div>)
  }

  return <div>{error}</div>
}

export type TextInputVariant = 'medium' | 'small'
export type TextInputState = 'default' | 'hovered' | 'focused' | 'error'

export type TextInputProps = {
  className?: string
  variant?: TextInputVariant
  /**
   * style override for the input, if undefined, the input will base its style
   * on its own internal input state
   */
  state?: TextInputState
  label?: string
  /**
   * if 'optional' will show "(Optional)" text at the end of the label
   * if 'required' will show a red asterisk (*) immediately after the label
   * if undefined, neither will be shown
   */
  requirement?: 'optional' | 'required'
  /**
   * will render a TooltipIcon next to the label
   */
  tooltipProps?: TooltipIconProps
  /**
   * custom tooltip element to render next to the label
   */
  tooltip?: React.ReactElement
  tooltipPosition?: 'left' | 'right'
  microcopy?: string
  placeholder?: string
  loadingState?: LoadingState
  disabled?: boolean
  errorText?: React.ReactNode
  iconLeft?: React.ReactElement
  iconRight?: React.ReactElement
  baseValue?: string
  testId?: string
  labelTestId?: string
  value?: string
}

type TextInputComponentProps = React.ComponentProps<'input'> & TextInputProps

export interface TextInputRef {
  focus: () => void
  container: HTMLDivElement | null
  input: HTMLInputElement | null
}

const TextInput = React.forwardRef<TextInputRef, TextInputComponentProps>(
  (props, ref): React.ReactElement => {
    const {
      className = '',
      variant = 'medium',
      state,
      label,
      requirement,
      tooltipPosition = 'right',
      tooltip,
      tooltipProps,
      microcopy,
      errorText,
      iconLeft,
      iconRight,
      baseValue,
      loadingState,
      disabled,
      testId,
      value,
      ...rest
    } = props

    const reactId = useId()
    const errorId = `${reactId}-error`
    const containerRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    useImperativeHandle(ref, () => ({
      focus: (options?: FocusOptions) => {
        inputRef.current?.focus(options)
      },
      get container() {
        return containerRef.current
      },
      get input() {
        return inputRef.current
      },
    }))

    const { showIdle, showPending, showSuccess } =
      useLoadingStateAnimation(loadingState)

    const translate = useTranslate()

    return (
      <div
        className={cn(className, variant)}
        css={style}
        data-testid={
          testId
            ? `${testId}-group`
            : label
              ? `${label.toLowerCase().replace(/\s+/g, '-')}-group`
              : ''
        }
        data-label={label}
        data-value={value}
      >
        <label
          className={cn('label', state, variant, {
            error: errorText || state === 'error' || loadingState === 'error',
          })}
        >
          {label && (
            <div className={'label-field'}>
              {tooltipPosition === 'left' && tooltip}
              {tooltipPosition === 'left' && tooltipProps && (
                <TooltipIcon {...tooltipProps} />
              )}
              <span data-testid={`${testId}-labelField`}>{label}</span>
              {requirement === 'required' && (
                <span className={'required-asterisk'}>{'*'}</span>
              )}
              {baseValue && (
                <span>
                  {translate('(was "${baseValue}")', {
                    baseValue,
                  })}
                </span>
              )}
              {tooltipPosition === 'right' && tooltip}
              {tooltipPosition === 'right' && tooltipProps && (
                <TooltipIcon {...tooltipProps} />
              )}
              {requirement === 'optional' && (
                <span>{translate('(Optional)')}</span>
              )}
            </div>
          )}
          <div
            data-testid={
              testId
                ? `${testId}-textbox`
                : label
                  ? `${label.toLowerCase().replace(/\s+/g, '-')}-textbox`
                  : ''
            }
            ref={containerRef}
            className={cn('textbox-container', variant, {
              disabled,
            })}
          >
            {iconLeft && (
              <div className={cn('textbox-icon', variant)}>{iconLeft}</div>
            )}
            <TextInputInternal
              variant={variant}
              disabled={disabled}
              value={value}
              {...rest}
              ref={inputRef}
              aria-invalid={!!errorText || state === 'error' || undefined}
              aria-describedby={errorText ? errorId : undefined}
              aria-required={requirement === 'required' || undefined}
              data-testid={
                testId
                  ? `${testId}-input`
                  : label
                    ? `${label.toLowerCase().replace(/\s+/g, '-')}-input`
                    : ''
              }
            />
            {loadingState ? (
              <div
                className={cn(
                  'textbox-icon',
                  'self-end',
                  'animation-container',
                  variant,
                )}
              >
                <Fade in={showIdle} timeout={350} unmountOnExit>
                  <Box className="animation-icon-container">{iconRight}</Box>
                </Fade>
                <Fade in={showPending} timeout={350} unmountOnExit>
                  <Box className="animation-icon-container">
                    <Processing width={16} height={16} viewBox={'0 0 24 24'} />
                  </Box>
                </Fade>
                <Fade in={showSuccess} timeout={350} unmountOnExit>
                  <Box className="animation-icon-container">
                    <LottieSuccessIcon play={showSuccess} freezeOnLastFrame />
                  </Box>
                </Fade>
              </div>
            ) : (
              iconRight && (
                <div className={cn('textbox-icon', 'self-end', variant)}>
                  {iconRight}
                </div>
              )
            )}
          </div>
        </label>
        {microcopy && (
          <div className={cn('helper-text', variant)}>{microcopy}</div>
        )}
        {errorText && (
          <div id={errorId} role="alert" className={cn('error-text', variant)}>
            {renderErrors(errorText)}
          </div>
        )}
      </div>
    )
  },
)

export default TextInput
